import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from "react"
import { useAuth } from "@/lib/auth-context"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { User, Send, Check, Copy, ExternalLink } from "lucide-react"
import { API_BASE } from '@/lib/api-base'

const PAIRING_POLL_MS = 3500

interface TelegramPairing {
  deep_link: string
  expires_at: string
}

function PerfilPage() {
  const { user, apiFetch, isSuperAdmin, refetchUser } = useAuth()
  const [pairing, setPairing] = useState<TelegramPairing | null>(null)
  const [isTelegramLinked, setIsTelegramLinked] = useState(user?.telegram_id != null)
  const [now, setNow] = useState(Date.now())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const apiFetchRef = useRef(apiFetch)
  const refetchUserRef = useRef(refetchUser)

  useEffect(() => {
    apiFetchRef.current = apiFetch
  }, [apiFetch])

  useEffect(() => {
    refetchUserRef.current = refetchUser
  }, [refetchUser])

  useEffect(() => {
    setIsTelegramLinked(user?.telegram_id != null)
  }, [user?.telegram_id])

  useEffect(() => {
    if (!pairing) return
    const interval = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(interval)
  }, [pairing])

  useEffect(() => {
    if (!pairing) return

    const expiresAt = Date.parse(pairing.expires_at)
    if (Number.isNaN(expiresAt) || Date.now() >= expiresAt) return

    let cancelled = false

    const checkLinked = async () => {
      if (cancelled || Date.now() >= expiresAt) return

      try {
        const res = await apiFetchRef.current(`${API_BASE}/api/auth/me`)
        if (!res.ok || cancelled) return
        const me = (await res.json()) as { telegram_id?: number | null }
        if (me.telegram_id == null) return

        cancelled = true
        await refetchUserRef.current()
        setIsTelegramLinked(true)
        setPairing(null)
        setError(null)
        setSuccess("Telegram vinculado!")
      } catch {
        // mantém polling em erros transitórios
      }
    }

    void checkLinked()
    const interval = window.setInterval(() => {
      if (Date.now() >= expiresAt) {
        window.clearInterval(interval)
        return
      }
      void checkLinked()
    }, PAIRING_POLL_MS)

    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [pairing])

  const pairingSeconds = pairing
    ? Math.max(0, Math.ceil((Date.parse(pairing.expires_at) - now) / 1000))
    : 0

  const handleVincularTelegram = async () => {
    setError(null)
    setSuccess(null)
    setLoading(true)

    try {
      const res = await apiFetch(`${API_BASE}/api/auth/telegram/pairings`, { method: "POST" })
      if (res.ok) {
        setPairing(await res.json() as TelegramPairing)
        setNow(Date.now())
      } else {
        const data = await res.json().catch(() => ({})) as { detail?: string }
        setError(data.detail || "Não foi possível gerar o link do Telegram.")
      }
    } catch {
      setError("Erro de conexão.")
    } finally {
      setLoading(false)
    }
  }

  const handleCopiarLink = async () => {
    if (!pairing) return
    try {
      await navigator.clipboard.writeText(pairing.deep_link)
      setSuccess("Link copiado.")
    } catch {
      setError("Não foi possível copiar o link. Copie-o manualmente.")
    }
  }

  const handleDesvincularTelegram = async () => {
    if (!window.confirm("Desvincular o Telegram? Isso encerrará suas outras sessões.")) return
    setError(null)
    setSuccess(null)
    setLoading(true)

    try {
      const res = await apiFetch(`${API_BASE}/api/auth/telegram`, { method: "DELETE" })
      if (res.ok) {
        setIsTelegramLinked(false)
        setPairing(null)
        setSuccess("Telegram desvinculado.")
      } else {
        const data = await res.json().catch(() => ({})) as { detail?: string }
        setError(data.detail || "Não foi possível desvincular o Telegram.")
      }
    } catch {
      setError("Erro de conexão.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <User className="h-8 w-8 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-bold">Meu Perfil</h1>
          <p className="text-muted-foreground text-sm">Gerencie suas informações e integrações</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Informações da Conta</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground">Nome</Label>
              <p className="font-medium">{user?.nome || "-"}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Email</Label>
              <p className="font-medium">{user?.email || "-"}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Tipo</Label>
              <Badge variant="secondary">{isSuperAdmin ? "Super Admin" : "Usuário"}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2"><Send className="h-5 w-5" />Telegram</CardTitle>
            <Badge variant={isTelegramLinked ? "default" : "secondary"} className={isTelegramLinked ? "bg-green-500" : ""}>
              {isTelegramLinked && <Check className="h-3 w-3 mr-1" />}{isTelegramLinked ? "Vinculado" : "Não vinculado"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && <div role="alert" className="p-3 bg-destructive/10 text-destructive rounded text-sm">{error}</div>}
          {success && <div className="p-3 bg-green-50 dark:bg-green-950/20 text-green-800 dark:text-green-200 rounded text-sm flex items-center gap-2"><Check className="h-4 w-4" />{success}</div>}

          {isTelegramLinked ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Seu Telegram está vinculado ao GranjaSmart.</p>
              <Button variant="destructive" onClick={handleDesvincularTelegram} disabled={loading}>Desvincular Telegram</Button>
            </div>
          ) : pairing && pairingSeconds > 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Abra o bot pelo link abaixo e envie a mensagem iniciada automaticamente. O link expira em {Math.floor(pairingSeconds / 60)}:{String(pairingSeconds % 60).padStart(2, "0")}.</p>
              <div className="flex gap-2">
                <Input readOnly value={pairing.deep_link} aria-label="Link de vinculação do Telegram" />
                <Button type="button" variant="outline" size="icon" onClick={handleCopiarLink} aria-label="Copiar link"><Copy className="h-4 w-4" /></Button>
              </div>
              <Button asChild><a href={pairing.deep_link} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4 mr-2" />Abrir no Telegram</a></Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Vincule seu Telegram para usar o bot da GranjaSmart e receber notificações.</p>
              {pairing && <p className="text-sm text-amber-700">O link expirou. Gere um novo para continuar.</p>}
              <Button onClick={handleVincularTelegram} disabled={loading}>{loading ? "Gerando link..." : "Vincular Telegram"}</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/perfil')({
  component: PerfilPage,
})
